import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShopBannerRow, useShopBannerList } from '../lib/api/shopBanners';
import { Button } from '../components/ui/Button';
import ShopBannerStatus, { RecurringStatusPill, dayBefore, fmtDay } from '../components/shopBanners/ShopBannerStatus';
import { LOCAL_TIME_NOTE, scheduleSentence } from '../components/shopBanners/schedule';
import styles from './ShopBanners.module.css';

/** When the banner is on the shop, in Brussels days; falls back to the
 *  placement dates (inclusive end) for banners not set up yet. */
const when = (r: ShopBannerRow) => {
  const b = r.banner;
  if (b?.start_date && b.end_date) {
    return { range: `${fmtDay(b.start_date)} → ${fmtDay(dayBefore(b.end_date))}`, note: `off at 00:00 on ${fmtDay(b.end_date)}` };
  }
  if (r.publish_date) {
    return { range: `${fmtDay(r.publish_date)} → ${fmtDay(r.end_date ?? r.publish_date)}`, note: 'placement dates' };
  }
  return { range: 'No dates yet', note: '' };
};

const GROUPS: { key: string; label: string; test: (r: ShopBannerRow) => boolean }[] = [
  { key: 'live', label: 'Live now', test: (r) => r.live_state === 'Live now' },
  { key: 'scheduled', label: 'Scheduled', test: (r) => r.live_state === 'Scheduled' },
  { key: 'todo', label: 'Not on the shop yet', test: (r) => r.live_state === 'Draft' && !r.ended },
  { key: 'ended', label: 'Ended', test: (r) => r.live_state === 'Ended' || (r.live_state === 'Draft' && r.ended) },
];

export const ShopBanners = () => {
  const [includeEnded, setIncludeEnded] = useState(false);
  const { data, isLoading } = useShopBannerList(includeEnded);
  const rows = data?.campaigns ?? [];
  const recurring = data?.recurring ?? [];
  const navigate = useNavigate();

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Planning</div>
          <h1 className="page-title">Shop banners &amp; timers</h1>
          <p className={styles.lede}>
            Banners on product pages: the campaigns' "Banner on PDP" placements, and the recurring Nightly and Weekend
            sales. Click one to edit the text, products and timing, and to send it to the shop. A countdown appears
            automatically near the end.
          </p>
          <p className={styles.lede}>
            <strong>Several banners on one product?</strong> The shop shows one, in this order: (1) a campaign banner
            whose countdown is running (its last hours, 48 by default), (2) a recurring banner inside its window
            (nightly / weekend), (3) a campaign banner without countdown. Within the same level: the highest priority,
            then the one ending first.
          </p>
        </div>
      </div>

      {isLoading && <div className={styles.empty}>Loading…</div>}

      {!isLoading && (
        <>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Campaigns</h2>
            <Button variant={includeEnded ? 'primary' : 'ghost'} onClick={() => setIncludeEnded((v) => !v)}>
              {includeEnded ? 'Hide ended' : 'Show ended'}
            </Button>
          </div>
          {rows.length === 0 && (
            <div className={styles.empty}>
              No "Banner on PDP" placements {includeEnded ? '' : 'live or coming up '}yet. Add one to a campaign to plan a product-page banner.
            </div>
          )}

          {GROUPS.map((g) => {
            const list = rows.filter(g.test);
            if (!list.length) return null;
            return (
              <section key={g.key} className={styles.group}>
                <h3 className={styles.groupTitle}>
                  {g.label} <span className={styles.count}>{list.length}</span>
                </h3>
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Banner</th>
                        <th>Products</th>
                        <th>On the shop (Brussels)</th>
                        <th>Priority</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((r) => {
                        const w = when(r);
                        return (
                          <tr key={r.placement_id} onClick={() => navigate(`/shop-banners/${r.placement_id}`)}>
                            <td>
                              <div className={styles.headline}>
                                {r.banner?.headline?.split('\n')[0] || <span className={styles.muted}>No text yet</span>}
                              </div>
                              <div className={styles.sub}>
                                {r.campaign ? (
                                  <Link to={`/campaigns/${r.campaign}`} onClick={(e) => e.stopPropagation()}>
                                    {r.campaign_name}
                                  </Link>
                                ) : 'No campaign'}
                                {r.placement_title && ` · ${r.placement_title}`}
                              </div>
                            </td>
                            <td>{r.banner?.collection_title || <span className={styles.muted}>—</span>}</td>
                            <td>
                              <div className="mono" style={{ fontSize: 12 }}>{w.range}</div>
                              {w.note && <div className={styles.sub}>{w.note}</div>}
                            </td>
                            <td className="mono">{r.banner ? r.banner.priority : '—'}</td>
                            <td>
                              <ShopBannerStatus state={r.live_state} inSync={r.in_sync} notSetUp={!r.banner} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}

          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Recurring</h2>
          </div>
          <p className={styles.lede} style={{ marginTop: -4, marginBottom: 10 }}>
            Every week on a fixed schedule. {LOCAL_TIME_NOTE}
          </p>
          {recurring.length === 0 ? (
            <div className={styles.empty}>No recurring banners.</div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Banner</th>
                    <th>Products</th>
                    <th>Schedule (local time)</th>
                    <th>Priority</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recurring.map((b) => (
                    <tr key={b.id} onClick={() => navigate(`/shop-banners/recurring/${b.id}`)}>
                      <td>
                        <div className={styles.headline}>{b.name}</div>
                        <div className={styles.sub}>{b.headline?.split('\n')[0] || 'No text yet'}</div>
                      </td>
                      <td>{b.collection_title || <span className={styles.muted}>All products</span>}</td>
                      <td>
                        <div className="mono" style={{ fontSize: 12 }}>
                          {scheduleSentence(b.repeat_days, b.repeat_start_minute, b.repeat_minutes)}
                        </div>
                        {b.paused_from && b.paused_until && (
                          <div className={styles.sub}>Pause {fmtDay(b.paused_from)} → {fmtDay(b.paused_until)}</div>
                        )}
                      </td>
                      <td className="mono">{b.priority}</td>
                      <td>
                        <RecurringStatusPill status={b.recurring_status} inSync={b.in_sync} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShopBanners;
